/**
 * PromptDiffView — line diff between two prompt revisions.
 *
 * Read-only and deliberately plain: a monospace gutter of +/− rows. The
 * interesting part is `diffPromptLines`; this only paints it.
 */

import { diffPromptLines, diffStat, type DiffRow } from './promptDiff'
import { cn } from '@/lib/utils'

const ROW_STYLES: Record<DiffRow['kind'], string> = {
  added: 'bg-success/10 text-success',
  removed: 'bg-destructive/10 text-destructive',
  unchanged: 'text-muted-foreground',
}

const MARKERS: Record<DiffRow['kind'], string> = {
  added: '+',
  removed: '-',
  unchanged: ' ',
}

interface PromptDiffProps {
  before: string
  after: string
  beforeLabel: string
  afterLabel: string
}

export function PromptDiffView({
  before,
  after,
  beforeLabel,
  afterLabel,
}: PromptDiffProps): React.JSX.Element {
  const rows = diffPromptLines(before, after)
  const { added, removed } = diffStat(rows)

  if (added === 0 && removed === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {beforeLabel} and {afterLabel} are identical.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {beforeLabel} → {afterLabel}
        <span className="ml-2 font-medium text-success">+{added}</span>
        <span className="ml-1 font-medium text-destructive">−{removed}</span>
      </p>
      <div className="max-h-96 overflow-auto rounded-md border bg-card">
        <pre className="text-xs leading-relaxed">
          {rows.map((row, i) => (
            <div
              key={`${String(i)}-${row.kind}`}
              className={cn('flex gap-2 px-3 py-px', ROW_STYLES[row.kind])}
            >
              <span aria-hidden className="select-none opacity-60">
                {MARKERS[row.kind]}
              </span>
              {/* Wrap rather than scroll horizontally: prompt lines are prose
                  paragraphs, not code, and are routinely hundreds of chars. */}
              <span className="whitespace-pre-wrap break-words">{row.text || ' '}</span>
            </div>
          ))}
        </pre>
      </div>
    </div>
  )
}
